/**
 * The ways out of an ad (docs/SPEC.md §13, §25.5; P24-T05): the estimate,
 * the outputs, a true render of a moment, the description.
 *
 * - `GET /v1/ads/{id}/outputs/estimate` → `{version, rt: {"720", "1080"},
 *   final: {credits, seconds, resolution}, still: {credits, seconds},
 *   refine: {provider} | null}`;
 * - `POST …/outputs {kind: rt | final, resolution, language?}` (202) →
 *   `{outputs: [Output]}`; `GET …/outputs` → `{items}`, newest first;
 *   `POST …/outputs/{oid}/cancel`;
 * - `POST …/stills {moment}` (202) → `{still, reused}`; `GET …/stills` →
 *   `{items, wait}`; `POST …/stills/{sid}/cancel`;
 * - `GET …/description?language=` → `{version, language, text, sentence}`.
 *
 * The socket's `output` (an output's state and progress), `frame` (a
 * rendered frame's thumbnail) and `still` (a true render's state) are written
 * into these queries by `applyOutputEvent`.
 */
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { SocketEvent } from "../ui/useSocket";
import { API_URL, api, newIdempotencyKey } from "./client";
import { ME_KEY } from "./me";

export type OutputKind = "rt" | "final" | "still";
export type OutputState =
  "queued" | "working" | "done" | "failed" | "cancelled";

/** An output as `GET …/outputs` shows it (`backend/ads/renders.summary`). */
export interface Output {
  id: string;
  ad_id?: string;
  version_id?: string;
  kind: OutputKind;
  state: OutputState;
  language: string;
  aspect: string;
  resolution: number;
  /** Frames rendered over the frames to render, 0–1. */
  progress: number;
  /** A true render's moment. */
  moment: { scene: string; t: number } | null;
  /** The file, presigned as an attachment, once it exists. */
  url: string | null;
  bytes?: number;
  duration_s?: number | null;
  credits_estimated?: number;
  credits_captured?: number;
  error_code: string | null;
  sentence?: string | null;
  created_at?: string | null;
  finished_at?: string | null;
}

export interface OutputsEstimate {
  version: number;
  rt: Record<string, number>;
  final: { credits: number; seconds: number; resolution: number };
  still: { credits: number; seconds: number };
  /** §30.6: the refinement provider's name, or null while none is set (the
   *  card "Refine with {provider}" is then not drawn). */
  refine?: { provider: string } | null;
}

/** What can be said of the wait for a render machine (Phase 13's). */
export interface StillWait {
  machine: "warm" | "cold" | "busy" | "unavailable";
  seconds: number | null;
}

export interface Description {
  version: number;
  language: string;
  text: string;
  sentence: string;
}

const ad = (id: string) => `/v1/ads/${encodeURIComponent(id)}`;
export const estimateKey = (id: string) => ["ad-estimate", id] as const;
export const outputsKey = (id: string) => ["ad-outputs", id] as const;
export const stillsKey = (id: string) => ["ad-stills", id] as const;
/** The last frame's thumbnail per output id, from the `frame` events. */
export const framesKey = (id: string) => ["ad-frames", id] as const;

export const ENDED: readonly OutputState[] = ["done", "failed", "cancelled"];
export const running = (o: Pick<Output, "state">) => !ENDED.includes(o.state);

/** Poll while something is being made: the socket brings every change, the
 *  poll covers a socket that dropped. */
export const OUTPUTS_POLL_MS = 5_000;

/** A link the API serves (relative to it) as an address the browser opens. */
export function apiLink(path: string): string {
  return /^[a-z][a-z0-9+.-]*:/i.test(path) ? path : `${API_URL}${path}`;
}

export function useOutputsEstimate(adId: string, enabled: boolean) {
  return useQuery({
    queryKey: estimateKey(adId),
    queryFn: () => api<OutputsEstimate>(`${ad(adId)}/outputs/estimate`),
    enabled,
    staleTime: 0,
    retry: false,
  });
}

export function useOutputs(adId: string) {
  return useQuery({
    queryKey: outputsKey(adId),
    queryFn: () => api<{ items: Output[] }>(`${ad(adId)}/outputs`),
    refetchInterval: (q) =>
      q.state.data?.items.some(running) ? OUTPUTS_POLL_MS : false,
  });
}

function putOutput(queryClient: QueryClient, adId: string, output: Output) {
  queryClient.setQueryData<{ items: Output[] }>(outputsKey(adId), (old) => ({
    items: [output, ...(old?.items ?? []).filter((o) => o.id !== output.id)],
  }));
}

export interface MakeOutput {
  kind: "rt" | "final";
  resolution: number;
  language?: string;
}

/** `POST …/outputs`: one way out, held and queued. */
export function useMakeOutput(adId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: MakeOutput) =>
      api<{ outputs: Output[] }>(`${ad(adId)}/outputs`, {
        method: "POST",
        // the button showed the estimate: pressing it is the yes (§15.1)
        body: { ...body, confirm: true },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: ({ outputs }) => {
      for (const o of [...outputs].reverse()) putOutput(queryClient, adId, o);
      void queryClient.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

export function useCancelOutput(adId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<Output>(`${ad(adId)}/outputs/${encodeURIComponent(id)}/cancel`, {
        method: "POST",
      }),
    onSuccess: (output) => putOutput(queryClient, adId, output),
  });
}

export function useAdStills(adId: string) {
  return useQuery({
    queryKey: stillsKey(adId),
    queryFn: () =>
      api<{ items: Output[]; wait: StillWait | null }>(`${ad(adId)}/stills`),
    refetchInterval: (q) =>
      q.state.data?.items.some(running) ? OUTPUTS_POLL_MS : false,
  });
}

function putStill(queryClient: QueryClient, adId: string, still: Output) {
  queryClient.setQueryData<{ items: Output[]; wait: StillWait | null }>(
    stillsKey(adId),
    (old) => ({
      wait: old?.wait ?? null,
      items: [still, ...(old?.items ?? []).filter((s) => s.id !== still.id)],
    }),
  );
}

/** `POST …/stills {moment}` → `{still, reused}`. */
export function useMakeStill(adId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      moment,
      idempotencyKey,
    }: {
      moment: { scene: string; t: number };
      idempotencyKey: string;
    }) =>
      api<{ still: Output; reused: boolean }>(`${ad(adId)}/stills`, {
        method: "POST",
        // the dialog showed the estimate: its button is the yes (§15.1)
        body: { moment, confirm: true },
        idempotencyKey,
      }),
    onSuccess: ({ still }) => putStill(queryClient, adId, still),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: stillsKey(adId) });
      void queryClient.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

/** `POST …/stills/{sid}/cancel` → the true render as it is now. */
export function useCancelAdStill(adId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      idempotencyKey,
    }: {
      id: string;
      idempotencyKey: string;
    }) =>
      api<Output>(`${ad(adId)}/stills/${encodeURIComponent(id)}/cancel`, {
        method: "POST",
        idempotencyKey,
      }),
    onSuccess: (still) => putStill(queryClient, adId, still),
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: stillsKey(adId) }),
  });
}

/** `GET …/description` in a language the film has. */
export function fetchDescription(
  adId: string,
  language?: string,
): Promise<Description> {
  const q = language ? `?language=${encodeURIComponent(language)}` : "";
  return api<Description>(`${ad(adId)}/description${q}`);
}

/** The socket's `output`, `frame` and `still` events into the queries. */
export function applyOutputEvent(
  queryClient: QueryClient,
  adId: string,
  event: SocketEvent,
): void {
  if (event.type === "output" && event.output) {
    const shown = event.output as Partial<Output> & { id: string };
    queryClient.setQueryData<{ items: Output[] }>(outputsKey(adId), (old) => {
      if (!old) return old;
      const known = old.items.find((o) => o.id === shown.id);
      if (!known) {
        void queryClient.invalidateQueries({ queryKey: outputsKey(adId) });
        return old;
      }
      return {
        items: old.items.map((o) =>
          o.id === shown.id ? { ...o, ...shown } : o,
        ),
      };
    });
    if (shown.state && ENDED.includes(shown.state)) {
      void queryClient.invalidateQueries({ queryKey: outputsKey(adId) });
      void queryClient.invalidateQueries({ queryKey: ME_KEY });
    }
  } else if (event.type === "frame" && typeof event.output_id === "string") {
    const url = typeof event.url === "string" ? event.url : null;
    if (url) {
      queryClient.setQueryData<Record<string, string>>(
        framesKey(adId),
        (old) => ({ ...(old ?? {}), [event.output_id as string]: url }),
      );
    }
  } else if (event.type === "still") {
    void queryClient.invalidateQueries({ queryKey: stillsKey(adId) });
  }
}
